<template>
    <div class="summary-modal">
        <el-dialog
            :model-value="WebProfileStore.isEditSummaryModalOpen"
            width="1200px"
            :show-close="false"
            class="custom-modal"
            :destroy-on-close="true"
            @close="close"
            @closed="onClosed"
            :title="
                $trans('web_profile.summary_for') +
                ' ' +
                (WebProfileStore.profile.name || '')
            "
        >
            <span class="dialog-cancel-text" @click="close">
                {{ $trans("profile_dossier.cancel") }}
                <el-icon><Close /></el-icon>
            </span>

            <el-button
                type="primary"
                @click="save"
                :disabled="safeContentLength === 0"
            >
                {{ $trans("web_profile.save") }}
            </el-button>

            <div class="table-container">
                <Editor
                    ref="myTinyEditor"
                    :key="WebProfileStore.profile?.id || 'editor'"
                    v-model="localSummary"
                    :init="tinyInit"
                    :tinymce-script-src="cdnSrc"
                    license-key="gpl"
                />

                <div class="char-counter">
                    <span>{{ safeContentLength }}/5000</span>&nbsp;
                    <span>{{ $trans("web_profile.characters_used") }}</span>
                </div>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import { mapStores } from "pinia";
import { useWebProfileStore } from "@/stores/WebProfileStore";
import { Close } from "@element-plus/icons-vue";
import Editor from "@tinymce/tinymce-vue";

export default {
    name: "EditSummaryModal",
    components: { Close, Editor },
    data() {
        return {
            localSummary: "",
            previousSummary: "",
            cdnSrc: '/vendor/tinymce/tinymce.min.js',
            tinyInit: {
                height: 300,
                menubar: false,
                branding: false,
                promotion: false,
                base_url: '/vendor/tinymce',
                suffix: '.min',
                license_key: 'gpl',
                plugins: 'lists link autoresize code',
                toolbar: 'undo redo | bold italic underline | bullist numlist | link | code | removeformat',
                default_link_target: '_blank',
                link_assume_external_targets: true,
                entity_encoding: 'raw',
                content_style: 'body { background: #fff; font-family: Barlow, sans-serif; font-size: 15px; font-weight: 700; }',
            },
        };
    },
    computed: {
        ...mapStores(useWebProfileStore),
        isModalOpen() {
            return this.WebProfileStore.isEditSummaryModalOpen;
        },
        safeContentLength() {
            return this.getPlainText(this.localSummary).length;
        },
    },
    watch: {
        isModalOpen(newVal) {
            if (newVal) {
                this.localSummary = this.WebProfileStore.profile?.summary || "";
                this.previousSummary = this.localSummary;
            }
        },
        localSummary(newContent, oldContent) {
            const plainText = this.getPlainText(newContent);
            if (plainText.length > 5000) {
                this.$message.warning(
                    this.$trans("web_profile.summary_too_long") || "Summary is too long."
                );
                this.$nextTick(() => {
                    this.localSummary = this.previousSummary;
                });
            } else {
                this.previousSummary = newContent;
            }
        },
    },
    methods: {
        getPlainText(html) {
            if (!html) return "";
            const doc = new DOMParser().parseFromString(html, 'text/html');
            let text = doc.body.textContent || "";
            text = text.replace(/<\/?[^>]+(>|$)/g, "");
            return text.replace(/\u00a0/g, ' ').replace(/\n/g, '').trim();
        },
        close() {
            this.WebProfileStore.closeEditSummaryModal();
        },
        onClosed() {
            this.localSummary = '';
            this.previousSummary = '';
        },
        async save() {
            if (this.safeContentLength === 0) {
                this.$message.warning(this.$trans("web_profile.enter_summary"));
                return;
            }
            try {
                await this.WebProfileStore.updateSummary(this.localSummary);
                this.$message.success(this.$trans("web_profile.summary_saved"));
            } catch (e) {
                this.$message.error(this.$trans("global.something_went_wrong"));
            }
        },
    },
};
</script>

<style scoped>
.summary-modal :deep(.el-dialog) {
    top: -100px !important;
}

:deep(.el-dialog__header) {
    padding-top: 15px !important;
}

.table-container {
    width: 100%;
    box-sizing: border-box;
    padding-bottom: 24px;
    padding-top: 10px;
    background-color: #ededed;
    font-family: "Barlow", sans-serif;
    padding-right: 25px;
}

:deep(.el-input) {
    width: 100%;
}

:deep(.el-input__wrapper.is-focus) {
    border: 2px solid #41abc4 !important;
    box-shadow: none !important;
}

.el-button {
    float: right;
    margin-top: -35px;
    background-color: #41abc4;
    border-color: #41abc4;
    border-radius: unset;
    width: 200px;
    font-size: 16px !important;
    font-weight: 700 !important;
}

.dialog-cancel-text {
    position: absolute;
    top: 5px;
    right: 21px;
    cursor: pointer;
    font-size: 14px;
    color: #909399;
    font-weight: 500;
    z-index: 2000;
    user-select: none;
}

.dialog-cancel-text:hover {
    color: #41abc4;
}

.dialog-cancel-text :deep(.el-icon) {
    top: 3px;
    transform: scale(1.4);
}

.char-counter {
    text-align: right;
    font-size: 16px !important;
    font-weight: 700 !important;
    color: #000;
    margin-top: 8px;
}

:deep(.el-textarea__inner) {
    min-width: 100% !important;
    padding-top: 10px;
    padding-bottom: 10px;
    font-size: 16px !important;
    font-weight: 700 !important;
}

:deep(.el-dialog__title) {
    font-size: 1.5rem;
}
</style>